import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, DropdownMenu, ErrorPanel, Icon, Loading, Skeleton, Typography } from "@/components/atoms";
import { DataTable, EmptyState, Pagination } from "@/components/molecules";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { VehicleMigrationOnboardModal } from "./modals/VehicleMigrationOnboardModal";
import { useOperator, usePagination, useSearch, useSorting } from "@/hooks";
import { useVehicleTypeMigrationCheck } from "@/hooks/useVehicleTypeMigrationCheck";
import { useArchiveVehicleMutation, useGetVehiclesQuery, useUpdateVehicleMutation, vehicleApi } from "@/redux/apis/vehicle/vehicleApi";
import { useAppDispatch } from "@/redux/hooks";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { addToast, getAssetUrl } from "@/utils";
import { VehicleTypesSetupModal } from "../VehicleTypesSetupModal";

export const LegacyMyVehiclesPanel = () => {
  const { id: operatorId } = useOperator();
  const navigate = useNavigate();
  const { sorting, setSorting } = useSorting();
  const { page, pageSize, handlePageClick, handleSizeChange } = usePagination("legacy-vehicles", 25);
  const [search] = useSearch();
  const dispatch = useAppDispatch();
  const [updateVehicle, { isLoading: isTransferring }] = useUpdateVehicleMutation();
  const [archiveVehicle] = useArchiveVehicleMutation();
  const [showOnboard, setShowOnboard] = useState(() => !localStorage.getItem("legacy-vm-onboard-dismissed"));
  const [showSetupModal, setShowSetupModal] = useState(false);
  const { needsMigration: needsTypeMigration, valid: typesValid, total: typesTotal } = useVehicleTypeMigrationCheck();

  const { data: response, isFetching, error } = useGetVehiclesQuery(
    {
      owned_by: "PROVIDER",
      needs_type_migration: true,
      setup_incomplete: false,
      page,
      page_size: pageSize,
      search: search ?? undefined,
    },
    { refetchOnMountOrArgChange: true }
  );

  const handleTransfer = useCallback(
    async (vehicle: Vehicle) => {
      if (needsTypeMigration) {
        setShowSetupModal(true);
        return;
      }

      try {
        await updateVehicle({
          id: vehicle.uuid,
          data: {
            identifier: vehicle.identifier,
            capacity_pax: vehicle.capacity.pax,
            capacity_bags: vehicle.capacity.bags,
            maintenance_fee: vehicle.maintenanceFee,
            setup_step: 1,
            documents: vehicle.documents.map((d) => ({ uuid: d.uuid, expiry_date: d.expiryDate, document_type: d.documentType })),
            categories: vehicle.categories.map((c) => ({ uuid: c.uuid })),
          },
        }).unwrap();

        navigate(`/operator/${operatorId}/vehicles/my-vehicles/add/${vehicle.uuid}`);
      } catch (e) {
        getErrorMessages(e as Parameters<typeof getErrorMessages>[0]).forEach((m) => addToast("danger", m));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateVehicle, navigate, operatorId, needsTypeMigration]
  );

  const handleArchive = useCallback(
    async (vehicle: Vehicle) => {
      try {
        await archiveVehicle(vehicle.uuid).unwrap();
        addToast("success", "Vehicle has been archived");
        dispatch(vehicleApi.util.invalidateTags(["Vehicle"]));
      } catch (e) {
        getErrorMessages(e as Parameters<typeof getErrorMessages>[0]).forEach((m) => addToast("danger", m));
      }
    },
    [archiveVehicle, dispatch]
  );

  const columns = useMemo(
    () => [
      { id: "year", header: "Year", accessorKey: "year", size: 80, sortDescFirst: false },
      { id: "make", header: "Make", accessorKey: "make", cell: (props: { row: { original: Record<string, unknown> } }) => props.row.original.make, size: 150, sortDescFirst: false },
      { id: "model", header: "Model", accessorKey: "model", size: 120, sortDescFirst: false },
      { id: "plateNo", header: "Plate No.", accessorKey: "plateNo", cell: (props: { row: { original: Record<string, unknown> } }) => props.row.original.plateNo, size: 150, sortDescFirst: false },
      { id: "nickname", header: "Nickname", accessorKey: "nickname", size: 150, sortDescFirst: false },
      { id: "color", header: "Color", cell: (props: { row: { original: Record<string, unknown> } }) => props.row.original.color, size: 120 },
      { id: "driver", header: "Assigned Driver", cell: (props: { row: { original: Record<string, unknown> } }) => props.row.original.driver, size: 150 },
      { id: "transfer", header: "Transfer Vehicle", cell: (props: { row: { original: Record<string, unknown> } }) => props.row.original.transfer, size: 150 },
      { id: "actions", header: "Action", cell: (props: { row: { original: Record<string, unknown> } }) => props.row.original.actions, maxSize: 75 },
    ],
    []
  );

  const data = useMemo(() => {
    if (!response) return [];
    return response.vehicles.map((vehicle) => ({
      year: vehicle.year?.toString() ?? "-",
      make: (
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded bg-neutral-gray">
            <img
              src={getAssetUrl(vehicle.model.avatar, { width: 36, height: 36, fit: "contain", background: "transparent" })}
              alt={vehicle.model.make}
              className="h-[18px] w-[18px] shrink-0"
            />
          </div>
          {vehicle.model.make}
        </div>
      ),
      model: vehicle.model.model,
      plateNo: (
        <Link to={`/operator/${operatorId}/vehicles-legacy/my-vehicles/${vehicle.uuid}`} className="flex items-center gap-1 hover:text-info">
          {vehicle.registrationNumber}
        </Link>
      ),
      nickname: vehicle.identifier || "-",
      color: vehicle.color ? (
        <div className="flex items-center gap-2 capitalize">
          {vehicle.color.toLowerCase().replace("_", " ")}
          <div className="h-3 w-3 rounded-full border border-black" style={{ backgroundColor: vehicle.color.toLowerCase().replace("_", "") }} />
        </div>
      ) : (
        "-"
      ),
      driver: vehicle.currentDriver ? (
        <div className="flex items-center gap-2">
          {vehicle.currentDriver.avatar ? (
            <img src={getAssetUrl(vehicle.currentDriver.avatar, { width: 48, height: 48, fit: "contain" })} className="h-6 w-6 rounded-full" alt={vehicle.currentDriver.name} />
          ) : (
            <Icon name="Profile" size="sm" variant="Bold" className="h-6 w-6 rounded-full bg-neutral-gray p-1 text-neutral-dark-gray" />
          )}
          <Typography className="line-clamp-1">{vehicle.currentDriver.name}</Typography>
        </div>
      ) : (
        "-"
      ),
      transfer: (
        <Button variant="primary" size="sm" className="w-full" onClick={() => handleTransfer(vehicle)} disabled={isTransferring}>
          Transfer
        </Button>
      ),
      actions: (
        <DropdownMenu button={<Icon name="options" isCustom size="lg" />} position="bottom-start" className="!flex max-w-[24px]">
          <Link to={`/operator/${operatorId}/vehicles-legacy/my-vehicles/${vehicle.uuid}`}>
            <DropdownMenu.Item>
              <Icon name="ArrowRight" className="flex-shrink-0 mr-2.5 text-primary-dark" size="sm" />
              <Typography className="text-primary-dark">View Vehicle</Typography>
            </DropdownMenu.Item>
          </Link>
          <DropdownMenu.Item onClick={() => handleTransfer(vehicle)}>
            <Icon name="ArrowSwapHorizontal" className="flex-shrink-0 mr-2.5 text-primary-dark" size="sm" />
            <Typography className="text-primary-dark">Transfer to New Vehicle Manager</Typography>
          </DropdownMenu.Item>
          <DropdownMenu.Item onClick={() => handleArchive(vehicle)}>
            <Icon name="Trash" className="flex-shrink-0 mr-2.5 text-danger" size="sm" />
            <Typography className="text-primary-dark">Archive Vehicle</Typography>
          </DropdownMenu.Item>
        </DropdownMenu>
      ),
    }));
  }, [response, operatorId, handleTransfer, handleArchive, isTransferring]);

  const handleDismissOnboard = () => {
    localStorage.setItem("legacy-vm-onboard-dismissed", "1");
    setShowOnboard(false);
  };

  if (error) return <ErrorPanel messages={getErrorMessages(error)} />;
  if (isFetching) return <Skeleton />;

  if (response && response.vehicles.length > 0)
    return (
      <div className="relative">
        {isTransferring && <Loading className="rounded-lg" />}
        <DataTable
          className="w-full overflow-x-auto [&_table]:table-auto [&_tr]:bg-white"
          data={data}
          columns={columns}
          sorting={sorting}
          setSorting={setSorting}
        />
        <Pagination
          onPageClick={handlePageClick}
          onSizeChange={handleSizeChange}
          currentPage={response.page.current}
          pageSize={response.page.size}
          totalItems={response.page.total}
          pageCount={response.page.count}
        />
        <VehicleMigrationOnboardModal open={showOnboard} onClose={handleDismissOnboard} />
        <VehicleTypesSetupModal open={showSetupModal} onClose={() => setShowSetupModal(false)} valid={typesValid} total={typesTotal} />
      </div>
    );

  const emptyDescription = search
    ? "No vehicles found matching your search."
    : "There are no vehicles to display.";

  return (
    <>
      <EmptyState title="No Vehicles" description={emptyDescription} />
      <VehicleMigrationOnboardModal open={showOnboard} onClose={handleDismissOnboard} />
      <VehicleTypesSetupModal open={showSetupModal} onClose={() => setShowSetupModal(false)} valid={typesValid} total={typesTotal} />
    </>
  );
};
